import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Grid,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import styles from './styles.module.css';
import AIImageService from 'services/AIImageService';
import { useTranslation } from 'react-i18next';

export default function AIImageSuggestionDialog({ open, handleClose, onSelect, suggestionKey }) {
  const { t } = useTranslation('popup');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [searchKey, setSearchKey] = useState(suggestionKey || '');

  const fetchSuggestions = useCallback(async (key) => {
    setLoading(true);
    try {
      const data = await AIImageService.getAIImages(key);
      setImages(data || []);
    } catch (error) {
      console.error('Failed to fetch AI images:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchKey(suggestionKey || '');
      fetchSuggestions(suggestionKey || '');
    }
  }, [open, suggestionKey, fetchSuggestions]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      fetchSuggestions(searchKey);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <div className={styles.headSec}>
        <h2 className={styles.editTitle}>{t('Image Suggestions')}</h2>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent style={{ padding: '20px', minHeight: '400px' }}>
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: '20px' }}>
          <Grid item xs={10}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder={t('Search for images...')}
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onKeyPress={handleSearch}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => fetchSuggestions(searchKey)} size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
        </Grid>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </div>
        ) : images.length > 0 ? (
          <Grid container spacing={2}>
            {images.map((img) => (
              <Grid item xs={6} sm={4} md={3} key={img.id}>
                <div 
                  className={styles.aiImageCard} 
                  onClick={() => onSelect(img.image)}
                >
                  <img src={img.image} alt={img.key} className={styles.aiImagePreview} />
                  <div className={styles.aiImageOverlay}>
                    <Typography variant="caption">{img.key}</Typography>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <Typography color="textSecondary">{t('no_ai_suggestions_found', 'No suggestions found.')}</Typography>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
